import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Wallet,
  PiggyBank,
  AlertTriangle,
  TrendingUp,
  Clock,
  Sparkles,
  CheckCircle,
  Gift,
} from "lucide-react";
import { toast } from "sonner";
import WealthsimpleStepsModal from "@/components/WealthsimpleStepsModal";

const InvestTodaySurvey = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [wealthsimpleModalOpen, setWealthsimpleModalOpen] = useState(false);

  const questions = [
    {
      id: "savings",
      icon: Wallet,
      emoji: "💰",
      title: "Current Financial Snapshot",
      question: "How much money do you currently have saved up? (Checking + savings combined)",
      options: [
        { value: "under100", label: "Less than $100" },
        { value: "100-500", label: "$100-500" },
        { value: "500-1500", label: "$500-1,500" },
        { value: "1500-5000", label: "$1,500-5,000" },
        { value: "5000-10000", label: "$5,000-10,000" },
        { value: "over10000", label: "More than $10,000" },
      ],
    },
    {
      id: "monthly",
      icon: PiggyBank,
      emoji: "📊",
      title: "Monthly Investment Capacity",
      question: "Realistically, how much could you invest EVERY MONTH without affecting your ability to pay bills or buy essentials?",
      options: [
        { value: "0", label: "$0 right now (but I want to learn)" },
        { value: "10-25", label: "$10-25 per month" },
        { value: "25-50", label: "$25-50 per month" },
        { value: "50-100", label: "$50-100 per month" },
        { value: "100-200", label: "$100-200 per month" },
        { value: "200-500", label: "$200-500 per month" },
        { value: "500+", label: "$500+ per month" },
      ],
    },
    {
      id: "emergency",
      icon: AlertTriangle,
      emoji: "🚨",
      title: "Emergency Fund Status",
      question: "If your laptop broke tomorrow and you needed $500 to replace it, how would you feel about covering it?",
      tip: "Financial tip: Ideally have $500-1000 emergency fund before investing heavily",
      isSpectrum: true,
      options: [
        { value: "1-stressed", label: "I'd have to use credit or borrow, no other option" },
        { value: "2-tight", label: "I could scrape together most of it, but it would hurt" },
        { value: "3-manageable", label: "I could cover it, but I'd feel the pinch" },
        { value: "4-comfortable", label: "I could handle it and still have savings left" },
        { value: "5-easy", label: "No problem at all, I have 3+ months saved" },
      ],
    },
    {
      id: "risk",
      icon: TrendingUp,
      emoji: "🎲",
      title: "Risk Tolerance",
      question: "You invest $500. Six months later, the market drops and it's worth $350. How do you feel?",
      isSpectrum: true,
      options: [
        { value: "1-panic", label: "Sell immediately, I can't handle watching it drop more" },
        { value: "2-anxious", label: "Very nervous, I'd probably sell soon if it doesn't recover" },
        { value: "3-uneasy", label: "Uncomfortable, but I'd wait and see what happens" },
        { value: "4-calm", label: "Calm. It's a long-term investment, dips happen" },
        { value: "5-excited", label: "Excited to buy more, stocks are on sale!" },
      ],
    },
    {
      id: "timeline",
      icon: Clock,
      emoji: "⏰",
      title: "Investment Timeline",
      question: "When do you think you'll need this money you're investing?",
      options: [
        { value: "1year", label: "Within 1 year (saving for something specific)" },
        { value: "1-3years", label: "1-3 years (medium-term goal)" },
        { value: "3-5years", label: "3-5 years (longer-term planning)" },
        { value: "5-10years", label: "5-10 years (building for post-graduation life)" },
        { value: "10+years", label: "10+ years (retirement/long-term wealth)" },
        { value: "unsure", label: "Not sure yet, just want to start growing money" },
      ],
    },
  ];

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  const handleAnswer = (value) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: value,
    }));
  };

  const getRecommendation = () => {
    const { savings, monthly, emergency, risk, timeline } = answers;
    
    // Determine readiness level
    let readiness = "beginner";
    let suggestions = [];
    
    // Check emergency fund (updated for spectrum values)
    if (emergency === "1-stressed" || emergency === "2-tight") {
      suggestions.push("Build a $500-1000 emergency fund before investing heavily");
      readiness = "prep";
    }
    
    // Check savings
    if (savings === "under100" || savings === "100-500") {
      suggestions.push("Focus on saving consistently first - even $25/week adds up");
    }
    
    // Investment capacity
    if (monthly === "0") {
      suggestions.push("Start by tracking expenses to find money to invest");
      readiness = "learning";
    } else if (monthly === "10-25" || monthly === "25-50") {
      suggestions.push("Perfect for starting with a micro-investing app or TFSA");
    } else {
      suggestions.push("You're in a great position to start building wealth!");
    }
    
    // Risk tolerance advice (updated for spectrum values)
    if (risk === "1-panic" || risk === "2-anxious") {
      suggestions.push("Consider low-risk investments like GICs or bond ETFs to start");
    } else if (risk === "5-excited") {
      suggestions.push("Your risk tolerance is high - diversify to protect gains");
    }
    
    // Timeline advice
    if (timeline === "1year") {
      suggestions.push("For short-term goals, keep money in high-interest savings");
    } else if (timeline === "10+years" || timeline === "5-10years") {
      suggestions.push("Long timeline = more room for growth investments like ETFs");
    }

    return {
      readiness,
      suggestions: suggestions.slice(0, 4),
    };
  };

  const handleComplete = () => {
    setShowResults(true);
    localStorage.setItem("investTodayCompleted", "true");
    localStorage.setItem("investTodayAnswers", JSON.stringify(answers));
    if (onComplete) onComplete(answers);
  };

  const recommendation = showResults ? getRecommendation() : null;

  if (showResults && recommendation) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="border-0 shadow-floating">
          <CardHeader className="text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-10 h-10 text-emerald-600" />
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 mb-2">
              Your Investment Readiness
            </Badge>
            <CardTitle className="text-2xl font-heading">
              {recommendation.readiness === "prep" && "Let's Get You Ready!"}
              {recommendation.readiness === "learning" && "Great Start - Keep Learning!"}
              {recommendation.readiness === "beginner" && "You're Ready to Start!"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <p className="font-medium text-navy-700">Your Personalized Next Steps:</p>
              {recommendation.suggestions.map((suggestion, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl"
                >
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-700 text-sm">{suggestion}</p>
                </div>
              ))}
            </div>

            {/* Get Your Free $25 CTA */}
            <div className="bg-[#00C853] rounded-xl p-6 text-center">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mx-auto mb-4">
                <Gift className="w-7 h-7 text-[#00C853]" />
              </div>
              <p className="text-white font-heading font-bold text-xl mb-2">
                Get free $25 towards your Wealthsimple TFSA account!
              </p>
              <p className="text-white/90 text-sm mb-4">
                Ready to put your plan into action? Open a Wealthsimple account and get $25 free.
              </p>
              <Button 
                onClick={() => setWealthsimpleModalOpen(true)}
                className="bg-white text-[#00C853] hover:bg-white/90 text-lg px-8 py-6 rounded-full font-bold shadow-lg"
                data-testid="get-free-25-btn"
              >
                Claim Free $25
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Wealthsimple Steps Modal */}
            <WealthsimpleStepsModal 
              open={wealthsimpleModalOpen} 
              onOpenChange={setWealthsimpleModalOpen} 
            />

            <div className="bg-navy-700 rounded-xl p-6 text-center">
              <p className="text-white font-heading font-bold mb-2">
                Want to explore more tools?
              </p>
              <p className="text-slate-300 text-sm mb-4">
                Check out our free resources and calculators to continue your journey.
              </p>
              <Button 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
                onClick={() => {
                  toast.success("Let's start building your wealth!");
                }}
              >
                Explore Resources
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <Card className="border-0 shadow-floating" data-testid="invest-today-survey">
      <CardHeader>
        <div className="flex items-center justify-between mb-2">
          <Badge className="bg-emerald-500 text-white">
            Invest Today!
          </Badge>
          <span className="text-sm text-slate-500">
            Question {currentStep + 1} of {questions.length}
          </span>
        </div>
        <Progress value={progress} className="h-2 mb-4" />
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
            <currentQuestion.icon className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <p className="text-2xl">{currentQuestion.emoji}</p>
          </div>
        </div>
        <CardTitle className="font-heading text-xl mt-4">
          {currentQuestion.title}
        </CardTitle>
        <p className="text-slate-600">{currentQuestion.question}</p>
        {currentQuestion.tip && (
          <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg mt-2">
            {currentQuestion.tip}
          </p>
        )}
      </CardHeader>
      <CardContent>
        <RadioGroup
          value={answers[currentQuestion.id] || ""}
          onValueChange={handleAnswer}
        >
          <div className="space-y-3">
            {currentQuestion.options.map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  answers[currentQuestion.id] === option.value
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-slate-200 hover:border-emerald-300"
                }`}
              >
                <RadioGroupItem value={option.value} />
                <span className="text-slate-700 text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        </RadioGroup>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t">
          <Button
            variant="outline"
            onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentStep < questions.length - 1 ? (
            <Button
              onClick={() => setCurrentStep((s) => s + 1)}
              disabled={!answers[currentQuestion.id]}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={!answers[currentQuestion.id]}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              See My Results
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default InvestTodaySurvey;
